/**
 * Hub Theme Registry
 *
 * Reads hub theme configuration from config.json → hubThemeConfig.
 * Each hub entry in config.json defines: name, tagline, logo filenames,
 * colors, fonts, and layout flags.
 *
 * The "extendsTheme" key lets a hub inherit from another (e.g. Destea
 * extends "default" so it only overrides logo/text, keeping Kwantu colours).
 *
 * ── How it works ────────────────────────────────────────────────
 * 1. config.json is the single source of truth for theme settings.
 * 2. Logo filenames in config.json are resolved to actual imports here.
 * 3. getHubTheme(hubCode) returns a fully-resolved theme object.
 * 4. ThemeContext calls getHubTheme() and also lazy-loads the matching
 *    CSS file from assets/css/hubs/<hubCode>.css for variable overrides.
 */

import config from '../config.json';

// ── Logo asset imports (keyed by filename from config.json) ─────────────────
import kwantuLogo from '../assets/logo/ic_launcher_foreground.png';
import kwantuWebLogo from '../assets/logo/KwantuIconWeb.png';
import desteaLogo from '../assets/logo/DesteaIconWeb.png';
import desteaWebLogo from '../assets/logo/destea_300px.jpg';
import ccsLogoVertical from '../assets/logo/ccs_logo_vertical_black.png';
import ccsLogoHorizontal from '../assets/logo/ccs_logo_horizontal_black.png';
import ccsLogoVerticalWhite from '../assets/logo/ccs_logo_vertical_white.png';
import ccsLogoHorizontalWhite from '../assets/logo/ccs_logo_horizontal_white.png';

// Map config.json logo filenames → imported assets
const logoMap = {
  'ic_launcher_foreground.png': kwantuLogo,
  'KwantuIconWeb.png': kwantuWebLogo,
  'DesteaIconWeb.png': desteaLogo,
  'destea_300px.jpg': desteaWebLogo,
  'ccs_logo_vertical_black.png': ccsLogoVertical,
  'ccs_logo_horizontal_black.png': ccsLogoHorizontal,
  'ccs_logo_vertical_white.png': ccsLogoVerticalWhite,
  'ccs_logo_horizontal_white.png': ccsLogoHorizontalWhite,
};

/**
 * Resolve a logo filename from config.json to an imported asset.
 * Falls back to the Kwantu logo if the filename is not mapped.
 */
function resolveLogo(filename) {
  if (!filename) return kwantuLogo;
  return logoMap[filename] || kwantuLogo;
}

// ── Read config ─────────────────────────────────────────────────────────────
const themeConfigs = config.hubThemeConfig || {};

/**
 * Deep-merge source into target (1-level deep for objects, shallow for rest).
 */
function mergeTheme(base, override) {
  const merged = { ...base };
  for (const key of Object.keys(override)) {
    if (
      override[key] &&
      typeof override[key] === 'object' &&
      !Array.isArray(override[key]) &&
      base[key] &&
      typeof base[key] === 'object'
    ) {
      merged[key] = { ...base[key], ...override[key] };
    } else {
      merged[key] = override[key];
    }
  }
  return merged;
}

/**
 * Build a resolved theme object from a config.json entry.
 * Handles `extendsTheme` inheritance.
 */
function buildTheme(hubCode) {
  const entry = themeConfigs[hubCode];
  if (!entry) return null;

  // Handle inheritance via extendsTheme
  let base = {};
  if (entry.extendsTheme && themeConfigs[entry.extendsTheme]) {
    base = buildTheme(entry.extendsTheme) || {};
  }

  const merged = mergeTheme(base, entry);

  return {
    hubCode,
    name: merged.name || 'Kwantu',
    tagline: merged.tagline ?? '',
    logo: resolveLogo(merged.logo),
    webLogo: resolveLogo(merged.webLogo),
    reverseLogo: merged.reverseLogo ? resolveLogo(merged.reverseLogo) : null,
    reverseWebLogo: merged.reverseWebLogo ? resolveLogo(merged.reverseWebLogo) : null,
    loginLabel: merged.loginLabel || 'Username',
    loginPlaceholder: merged.loginPlaceholder || 'Enter your username',
    colors: {
      primary: '#0e9191',
      primaryDark: '#0a6e6e',
      accent: '#4f1787',
      secondary: '#161616',
      background: '#ffffff',
      text: '#333333',
      loginGradient: 'linear-gradient(135deg, #d8e3ea 0%, #c6d9db 100%)',
      brandGradient: 'linear-gradient(160deg, #0e9191 0%, #0a6e6e 100%)',
      ...(merged.colors || {}),
    },
    fonts: {
      base: '"Inter", sans-serif',
      heading: '"Inter", sans-serif',
      ...(merged.fonts || {}),
    },
    layout: {
      showRegistrationLink: false,
      showChatWidget: true,
      loginBrandPanelVisible: true,
      ...(merged.layout || {}),
    },
  };
}

// ── Pre-build the default theme ─────────────────────────────────────────────
const defaultTheme = buildTheme('default') || {
  hubCode: 'default',
  name: 'Kwantu',
  tagline: 'Your platform for data-driven community development',
  logo: kwantuLogo,
  webLogo: kwantuWebLogo,
  reverseLogo: null,
  reverseWebLogo: null,
  loginLabel: 'Username',
  loginPlaceholder: 'Enter your username',
  colors: {
    primary: '#0e9191',
    primaryDark: '#0a6e6e',
    accent: '#4f1787',
    secondary: '#161616',
    background: '#ffffff',
    text: '#333333',
    loginGradient: 'linear-gradient(135deg, #d8e3ea 0%, #c6d9db 100%)',
    brandGradient: 'linear-gradient(160deg, #0e9191 0%, #0a6e6e 100%)',
  },
  fonts: { base: '"Inter", sans-serif', heading: '"Inter", sans-serif' },
  layout: { showRegistrationLink: false, showChatWidget: true, loginBrandPanelVisible: true },
};

/**
 * Resolve a hub theme by hubCode.
 * Falls back to the default theme for unknown codes.
 */
export function getHubTheme(hubCode) {
  if (!hubCode) return defaultTheme;
  return buildTheme(hubCode) || defaultTheme;
}

export { defaultTheme };
export default themeConfigs;
